/**
 * Field contour map: all reservoir contours together, left list + tools panel.
 * Map vertices are geographic lat/lng for Leaflet; stored MB contour/wells use
 * local km relative to an explicit datum (lat/lng) set here.
 */
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  useMap,
  useMapEvents,
  ZoomControl,
} from 'react-leaflet';

import { MapBaseLayers } from '@/components/Map/mapLayers';
import { CommitNumberField } from '@/components/Scenarios/CommitNumberField';
import {
  FIELD_MARKER_ZOOM,
  MENA_DEFAULT_ZOOM,
  MENA_MAP_CENTER,
} from '@/constants/fieldMap';
import type { MbContourOrigin } from '@/types/materialBalance';
import type { LatLng } from '@/utils/fieldCoordinate';
import { parsePointCoordinate } from '@/utils/fieldCoordinate';
import {
  isValidGeoOrigin,
  latLngToLocalKm,
  localKmToLatLng,
} from '@/utils/localKmProjection';
import {
  contourFromGeoJSON,
  contourToGeoJSON,
  downloadGeoJSON,
  type ContourCoordFormat,
} from '@/utils/materialBalanceGeojson';

export type LatLngTuple = [number, number];

export const CONTOUR_COLORS = [
  '#1565c0',
  '#c62828',
  '#2e7d32',
  '#6a1b9a',
  '#ef6c00',
  '#00838f',
  '#ad1457',
  '#4527a0',
  '#558b2f',
  '#00695c',
] as const;

export function contourColorForIndex(index: number): string {
  return CONTOUR_COLORS[index % CONTOUR_COLORS.length];
}

export type WellsFieldMapContour = {
  id: string;
  name: string;
  color: string;
  vertices: LatLngTuple[];
};

export interface WellsFieldMapWell {
  id: string;
  name: string;
  lat: number;
  lng: number;
  kind: 'prod' | 'inj';
  /** Contour / layer id — used to hide wells with their contour */
  contourId?: string | null;
}

export interface WellsFieldMapProps {
  contours: WellsFieldMapContour[];
  selectedId: string;
  onSelectId: (id: string) => void;
  /** Editable vertices of the selected contour (geographic lat/lng for the map). */
  vertices: LatLngTuple[];
  onVerticesChange: (pts: LatLngTuple[]) => void;
  /** Fixed geographic datum; contour + wells are stored as km relative to this. */
  datum: MbContourOrigin | null;
  onDatumChange: (datum: MbContourOrigin) => void;
  wells?: WellsFieldMapWell[];
  flyToCoordinate?: string | null;
  readOnly?: boolean;
  saving?: boolean;
  onSaveContour?: () => void;
  dirty?: boolean;
}

function centroid(pts: LatLngTuple[]): LatLngTuple | null {
  if (!pts.length) return null;
  const lat = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lng = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  return [lat, lng];
}

function labelIcon(name: string, color: string, selected: boolean) {
  return L.divIcon({
    className: 'wells-contour-label',
    html: `<span style="
      display:inline-block;
      max-width:140px;
      padding:2px 8px;
      border-radius:4px;
      background:${selected ? color : 'rgba(255,255,255,0.92)'};
      color:${selected ? '#fff' : '#1a1a1a'};
      border:1px solid ${color};
      font:600 11px/1.3 system-ui,sans-serif;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
      box-shadow:0 1px 3px rgba(0,0,0,0.2);
      pointer-events:none;
    ">${name.replace(/</g, '&lt;')}</span>`,
    iconSize: [1, 1],
    iconAnchor: [0, 0],
  });
}

function ClickAddVertex({
  enabled,
  onAdd,
  suppressRef,
  draggingRef,
}: {
  enabled: boolean;
  onAdd: (p: LatLng) => void;
  suppressRef: React.MutableRefObject<boolean>;
  draggingRef: React.MutableRefObject<number | null>;
}) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      if (draggingRef.current != null) return;
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      onAdd({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

/**
 * Drag selected-contour vertices via CircleMarker + map pointer events.
 * Same approach as TrainPolygonEditor (Marker.draggable is unreliable in Firefox/macOS).
 */
function ContourVertexEditor({
  vertices,
  color,
  onMove,
  suppressMapClickRef,
  draggingRef,
}: {
  vertices: LatLngTuple[];
  color: string;
  onMove: (index: number, next: LatLngTuple) => void;
  suppressMapClickRef: React.MutableRefObject<boolean>;
  draggingRef: React.MutableRefObject<number | null>;
}) {
  const map = useMap();
  const movedRef = useRef(false);

  const endDrag = useCallback(() => {
    if (draggingRef.current == null) return;
    draggingRef.current = null;
    map.dragging.enable();
    map.touchZoom.enable();
    map.boxZoom.enable();
    map.keyboard.enable();
    map.getContainer().style.cursor = 'crosshair';
    suppressMapClickRef.current = true;
    movedRef.current = false;
  }, [draggingRef, map, suppressMapClickRef]);

  useMapEvents({
    mousemove(e) {
      const idx = draggingRef.current;
      if (idx == null) return;
      movedRef.current = true;
      onMove(idx, [e.latlng.lat, e.latlng.lng]);
    },
    mouseup() {
      endDrag();
    },
  });

  useEffect(() => {
    const onUp = () => endDrag();
    const onMoveWindow = (ev: MouseEvent | TouchEvent) => {
      const idx = draggingRef.current;
      if (idx == null) return;
      if ('touches' in ev && ev.cancelable) {
        ev.preventDefault();
      }
      let clientX: number;
      let clientY: number;
      if ('touches' in ev) {
        if (!ev.touches.length) return;
        clientX = ev.touches[0].clientX;
        clientY = ev.touches[0].clientY;
      } else {
        clientX = ev.clientX;
        clientY = ev.clientY;
      }
      const rect = map.getContainer().getBoundingClientRect();
      const point = L.point(clientX - rect.left, clientY - rect.top);
      const latlng = map.containerPointToLatLng(point);
      movedRef.current = true;
      onMove(idx, [latlng.lat, latlng.lng]);
    };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    document.addEventListener('mousemove', onMoveWindow);
    document.addEventListener('touchmove', onMoveWindow, { passive: false });
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
      document.removeEventListener('mousemove', onMoveWindow);
      document.removeEventListener('touchmove', onMoveWindow);
    };
  }, [draggingRef, endDrag, map, onMove]);

  const startDrag = (index: number, originalEvent: Event) => {
    L.DomEvent.preventDefault(originalEvent);
    L.DomEvent.stopPropagation(originalEvent);
    suppressMapClickRef.current = true;
    movedRef.current = false;
    draggingRef.current = index;
    map.dragging.disable();
    map.touchZoom.disable();
    map.boxZoom.disable();
    map.keyboard.disable();
    map.getContainer().style.cursor = 'grabbing';
  };

  return (
    <>
      {vertices.map((pos, idx) => (
        <CircleMarker
          key={`v-${idx}`}
          center={pos}
          radius={7}
          pathOptions={{
            color,
            weight: 2,
            fillColor: '#fff',
            fillOpacity: 1,
            bubblingMouseEvents: false,
          }}
          eventHandlers={{
            mousedown(e) {
              startDrag(idx, e.originalEvent);
            },
            touchstart(e) {
              startDrag(idx, e.originalEvent);
            },
            click(e) {
              L.DomEvent.stopPropagation(e.originalEvent);
              suppressMapClickRef.current = true;
            },
          }}
        />
      ))}
    </>
  );
}

function FitAllContours({
  contours,
  flyToCoordinate,
}: {
  contours: WellsFieldMapContour[];
  flyToCoordinate?: string | null;
}) {
  const map = useMap();
  const didFit = useRef(false);
  useEffect(() => {
    if (didFit.current) return;
    const all = contours.flatMap((c) => c.vertices);
    if (all.length >= 2) {
      map.fitBounds(L.latLngBounds(all.map(([lat, lng]) => [lat, lng])).pad(0.25), {
        animate: false,
      });
      didFit.current = true;
      return;
    }
    const pt = parsePointCoordinate(flyToCoordinate);
    if (pt) {
      map.setView([pt.lat, pt.lng], Math.max(map.getZoom(), FIELD_MARKER_ZOOM), {
        animate: false,
      });
      didFit.current = true;
    }
  }, [map, contours, flyToCoordinate]);
  return null;
}

function FitSelectedContour({
  selectedId,
  vertices,
}: {
  selectedId: string;
  vertices: LatLngTuple[];
}) {
  const map = useMap();
  const prevId = useRef(selectedId);
  useEffect(() => {
    if (prevId.current === selectedId) return;
    prevId.current = selectedId;
    if (vertices.length >= 2) {
      map.fitBounds(L.latLngBounds(vertices.map(([lat, lng]) => [lat, lng])).pad(0.2), {
        animate: true,
      });
    }
  }, [map, selectedId, vertices]);
  return null;
}

function FitOnImport({ vertices }: { vertices: LatLngTuple[] }) {
  const map = useMap();
  const prevLen = useRef(0);
  useEffect(() => {
    if (vertices.length >= 3 && prevLen.current < 3 && vertices.length - prevLen.current >= 3) {
      map.fitBounds(L.latLngBounds(vertices.map(([lat, lng]) => [lat, lng])).pad(0.2), {
        animate: true,
      });
    }
    prevLen.current = vertices.length;
  }, [map, vertices]);
  return null;
}

export const WellsFieldMap: React.FC<WellsFieldMapProps> = ({
  contours,
  selectedId,
  onSelectId,
  vertices,
  onVerticesChange,
  datum,
  onDatumChange,
  wells = [],
  flyToCoordinate = null,
  readOnly = false,
  saving = false,
  onSaveContour,
  dirty = false,
}) => {
  const [mapReady, setMapReady] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [pendingImportFormat, setPendingImportFormat] = useState<ContourCoordFormat | null>(
    null,
  );
  /** Contour id → visible on map; missing key = visible (default on) */
  const [visibleById, setVisibleById] = useState<Record<string, boolean>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const suppressMapClickRef = useRef(false);
  const draggingRef = useRef<number | null>(null);
  const verticesRef = useRef(vertices);
  verticesRef.current = vertices;
  const datumRef = useRef(datum);
  datumRef.current = datum;

  useEffect(() => {
    const t = window.setTimeout(() => setMapReady(true), 0);
    return () => window.clearTimeout(t);
  }, []);

  const isContourVisible = useCallback(
    (id: string) => visibleById[id] !== false,
    [visibleById],
  );

  const selected = contours.find((c) => c.id === selectedId) ?? null;
  const selectedColor = selected?.color ?? CONTOUR_COLORS[0];
  const closed = vertices.length >= 3;
  const selectedVisible = !!selectedId && isContourVisible(selectedId);
  const canEditVertices = !readOnly && selectedVisible;
  const hasDatum = isValidGeoOrigin(datum);

  const handleVertexMove = useCallback(
    (index: number, nextPos: LatLngTuple) => {
      const next = [...verticesRef.current];
      if (!next[index]) return;
      next[index] = nextPos;
      onVerticesChange(next);
    },
    [onVerticesChange],
  );

  const center = useMemo((): LatLngTuple => {
    if (isValidGeoOrigin(datum)) return [datum.lat, datum.lng];
    const pt = parsePointCoordinate(flyToCoordinate);
    if (pt) return [pt.lat, pt.lng];
    const first = contours.find((c) => c.vertices.length)?.vertices[0];
    if (first) return first;
    return [MENA_MAP_CENTER.lat, MENA_MAP_CENTER.lng];
  }, [flyToCoordinate, contours, datum]);

  const handleAdd = useCallback(
    (p: LatLng) => {
      if (!selectedId) return;
      onVerticesChange([...vertices, [p.lat, p.lng]]);
    },
    [onVerticesChange, vertices, selectedId],
  );

  const openImportPicker = (format: ContourCoordFormat) => {
    if (format === 'local_km' && !isValidGeoOrigin(datumRef.current)) {
      window.alert(
        'Set the datum latitude/longitude first. Local-km GeoJSON is placed relative to that point.',
      );
      return;
    }
    setPendingImportFormat(format);
    setImportDialogOpen(false);
    window.setTimeout(() => fileRef.current?.click(), 0);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const format = pendingImportFormat;
    e.target.value = '';
    setPendingImportFormat(null);
    if (!file || !format) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result));
        const pts = contourFromGeoJSON(data);
        if (format === 'geographic') {
          // GeoJSON [lon, lat] → map [lat, lng]
          onVerticesChange(pts.map((p) => [p.y, p.x] as LatLngTuple));
          return;
        }
        const origin = datumRef.current;
        if (!isValidGeoOrigin(origin)) {
          window.alert('Set the datum latitude/longitude before importing local-km GeoJSON.');
          return;
        }
        // File [x_km, y_km] → map lat/lng via datum
        onVerticesChange(
          pts.map((p) => {
            const g = localKmToLatLng(p.x, p.y, origin);
            return [g.lat, g.lng] as LatLngTuple;
          }),
        );
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Failed to parse GeoJSON');
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleExport = () => {
    if (vertices.length < 3) {
      window.alert('Need at least 3 contour points to export');
      return;
    }
    if (!isValidGeoOrigin(datum)) {
      window.alert('Set the datum latitude/longitude before exporting local-km GeoJSON.');
      return;
    }
    const localPts = vertices.map(([lat, lng]) => latLngToLocalKm(lat, lng, datum));
    const name = (selected?.name || 'contour').replace(/\s+/g, '_');
    downloadGeoJSON(`${name}.geojson`, contourToGeoJSON(localPts, name, 'local_km'));
  };

  const displayContours = useMemo(() => {
    return contours
      .filter((c) => isContourVisible(c.id))
      .map((c) => (c.id === selectedId ? { ...c, vertices } : c));
  }, [contours, selectedId, vertices, isContourVisible]);

  const displayWells = useMemo(
    () =>
      wells.filter(
        (w) => !w.contourId || isContourVisible(w.contourId),
      ),
    [wells, isContourVisible],
  );

  return (
    <Paper sx={{ p: 2.5, mb: 3, border: 1, borderColor: 'divider' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Field map</Typography>
        <Typography variant="body2" color="text.secondary">
          Set a geographic datum (lat/lng), then draw or import the contour. Contour nodes and
          wells are stored as kilometres relative to that datum.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'stretch',
          height: 480,
        }}
      >
        {/* Left panel: ~1/4 width, same height as map */}
        <Paper
          variant="outlined"
          sx={{
            width: '25%',
            minWidth: 200,
            maxWidth: 320,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
            Contours
          </Typography>
          <List
            dense
            sx={{
              flex: 1,
              overflowY: 'auto',
              py: 0,
              minHeight: 0,
            }}
          >
            {contours.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
                No reservoirs yet
              </Typography>
            ) : (
              contours.map((c) => {
                const selectedRow = c.id === selectedId;
                const visible = isContourVisible(c.id);
                const n = c.id === selectedId ? vertices.length : c.vertices.length;
                return (
                  <ListItemButton
                    key={c.id}
                    selected={selectedRow}
                    onClick={() => onSelectId(c.id)}
                    sx={{
                      alignItems: 'flex-start',
                      borderLeft: 3,
                      borderColor: selectedRow ? c.color : 'transparent',
                      opacity: visible ? 1 : 0.55,
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={visible}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        const checked = e.target.checked;
                        setVisibleById((prev) => ({ ...prev, [c.id]: checked }));
                      }}
                      sx={{ p: 0.25, mr: 0.5, mt: 0.15 }}
                      inputProps={{ 'aria-label': `Show ${c.name} on map` }}
                    />
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: c.color,
                        mt: 0.7,
                        mr: 1.25,
                        flexShrink: 0,
                      }}
                    />
                    <ListItemText
                      primary={c.name}
                      secondary={
                        n === 0
                          ? 'No contour'
                          : n < 3
                            ? `${n} pts · incomplete`
                            : `${n} pts`
                      }
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                );
              })
            )}
          </List>

          <Stack
            spacing={1}
            sx={{
              flexShrink: 0,
              p: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {selected
                ? `Editing: ${selected.name}`
                : 'Select a contour to edit'}
              {dirty ? ' · unsaved' : ''}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Datum (lat / lng)
            </Typography>
            <Stack direction="row" spacing={1}>
              <CommitNumberField
                size="small"
                label="Lat"
                value={datum?.lat ?? null}
                onCommit={(v) => {
                  if (v == null) return;
                  const fallback = parsePointCoordinate(flyToCoordinate);
                  onDatumChange({
                    lat: v,
                    lng: isValidGeoOrigin(datum)
                      ? datum.lng
                      : (fallback?.lng ?? MENA_MAP_CENTER.lng),
                  });
                }}
                allowEmpty={false}
                disabled={readOnly || saving || !selectedId}
                formatCommitted={(n) => Math.round(n * 1e6) / 1e6}
                inputProps={{ step: 'any' }}
                fullWidth
              />
              <CommitNumberField
                size="small"
                label="Lng"
                value={datum?.lng ?? null}
                onCommit={(v) => {
                  if (v == null) return;
                  const fallback = parsePointCoordinate(flyToCoordinate);
                  onDatumChange({
                    lat: isValidGeoOrigin(datum)
                      ? datum.lat
                      : (fallback?.lat ?? MENA_MAP_CENTER.lat),
                    lng: v,
                  });
                }}
                allowEmpty={false}
                disabled={readOnly || saving || !selectedId}
                formatCommitted={(n) => Math.round(n * 1e6) / 1e6}
                inputProps={{ step: 'any' }}
                fullWidth
              />
            </Stack>
            {!hasDatum && (
              <Typography variant="caption" color="warning.main">
                Required before save / local-km import
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={readOnly || saving || !selectedId}
              onClick={() => setImportDialogOpen(true)}
              fullWidth
            >
              Import GeoJSON
            </Button>
            <input
              ref={fileRef}
              hidden
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              onChange={handleImportFile}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleExport}
              disabled={vertices.length < 3}
              fullWidth
            >
              Export
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={readOnly || saving || !selectedId || vertices.length === 0}
              onClick={() => onVerticesChange(vertices.slice(0, -1))}
              fullWidth
            >
              Undo point
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={readOnly || saving || !selectedId || vertices.length === 0}
              onClick={() => onVerticesChange([])}
              fullWidth
            >
              Clear
            </Button>
            {onSaveContour && (
              <Button
                size="small"
                variant="contained"
                disabled={
                  readOnly ||
                  saving ||
                  !dirty ||
                  vertices.length < 3 ||
                  !selectedId ||
                  !hasDatum
                }
                onClick={() => onSaveContour()}
                fullWidth
              >
                {saving ? 'Saving…' : 'Save contour'}
              </Button>
            )}
          </Stack>
        </Paper>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            borderRadius: 1,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
            '& .leaflet-container': {
              height: '100%',
              width: '100%',
              zIndex: 0,
              ...(!readOnly && selectedId ? { cursor: 'crosshair' } : {}),
            },
            '& .wells-contour-label': {
              background: 'transparent',
              border: 'none',
            },
          }}
        >
          {mapReady ? (
            <MapContainer
              center={center}
              zoom={parsePointCoordinate(flyToCoordinate) ? FIELD_MARKER_ZOOM : MENA_DEFAULT_ZOOM}
              minZoom={2}
              maxZoom={22}
              scrollWheelZoom
              zoomControl={false}
              style={{ height: '100%', width: '100%' }}
            >
              <MapBaseLayers />
              <ZoomControl position="bottomright" />
              <FitAllContours contours={displayContours} flyToCoordinate={flyToCoordinate} />
              <FitSelectedContour selectedId={selectedId} vertices={vertices} />
              <FitOnImport vertices={vertices} />
              {canEditVertices && (
                <ClickAddVertex
                  enabled={canEditVertices}
                  onAdd={handleAdd}
                  suppressRef={suppressMapClickRef}
                  draggingRef={draggingRef}
                />
              )}

              {displayContours.map((c) => {
                const isSel = c.id === selectedId;
                const pts = c.vertices;
                if (pts.length < 2) return null;
                const pathOpts = {
                  color: c.color,
                  weight: isSel ? 3.5 : 2,
                  fillColor: c.color,
                  fillOpacity: isSel ? 0.22 : 0.08,
                  opacity: isSel ? 1 : 0.75,
                  dashArray: pts.length < 3 ? '4 4' : undefined,
                };
                const mid = centroid(pts);
                return (
                  <React.Fragment key={c.id}>
                    {pts.length >= 3 ? (
                      <Polygon
                        positions={pts}
                        pathOptions={pathOpts}
                        eventHandlers={{
                          click: (e) => {
                            L.DomEvent.stopPropagation(e);
                            onSelectId(c.id);
                          },
                        }}
                      />
                    ) : (
                      <Polyline
                        positions={pts}
                        pathOptions={pathOpts}
                        eventHandlers={{
                          click: (e) => {
                            L.DomEvent.stopPropagation(e);
                            onSelectId(c.id);
                          },
                        }}
                      />
                    )}
                    {mid && (
                      <Marker
                        position={mid}
                        icon={labelIcon(c.name, c.color, isSel)}
                        interactive={false}
                        zIndexOffset={isSel ? 1000 : 100}
                      />
                    )}
                  </React.Fragment>
                );
              })}

              {canEditVertices && (
                <ContourVertexEditor
                  vertices={vertices}
                  color={selectedColor}
                  onMove={handleVertexMove}
                  suppressMapClickRef={suppressMapClickRef}
                  draggingRef={draggingRef}
                />
              )}
              {!canEditVertices &&
                selectedVisible &&
                vertices.map((pos, idx) => (
                  <CircleMarker
                    key={`v-${idx}`}
                    center={pos}
                    radius={6}
                    pathOptions={{
                      color: selectedColor,
                      weight: 2,
                      fillColor: '#fff',
                      fillOpacity: 1,
                    }}
                  />
                ))}

              {displayWells.map((w) => (
                <CircleMarker
                  key={w.id}
                  center={[w.lat, w.lng]}
                  radius={7}
                  pathOptions={{
                    color: '#0f172a',
                    weight: 1,
                    fillColor: w.kind === 'inj' ? '#2563eb' : '#16a34a',
                    fillOpacity: 1,
                  }}
                />
              ))}
            </MapContainer>
          ) : null}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {selectedId
          ? `${vertices.length} point${vertices.length === 1 ? '' : 's'} on selected contour${
              closed ? ' · closed' : vertices.length ? ' · need ≥3' : ''
            }${canEditVertices ? ' · drag vertices to reshape' : ''}`
          : 'Select a contour to edit'}
      </Typography>

      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import GeoJSON coordinates</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How are coordinates stored in the file? Geographic values become kilometres relative to
            the datum on save; local-km values are placed using the current datum.
          </Typography>
          <Stack spacing={1}>
            <Button
              variant="outlined"
              onClick={() => openImportPicker('geographic')}
              fullWidth
            >
              Geographic (longitude, latitude)
            </Button>
            <Button
              variant="outlined"
              onClick={() => openImportPicker('local_km')}
              fullWidth
              disabled={!hasDatum}
            >
              Local km (x, y from datum)
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
